import React, { createContext, useEffect, useState } from 'react'
import type { PageSection } from '../layouts/components/Sidebar'

// SidebarContext context
interface SidebarContextType {
  pageSections: PageSection[]
  setPageSections: React.Dispatch<React.SetStateAction<PageSection[] | undefined>>,

  // Curent waypoint
  currentWaypoint: string | number | undefined
  setCurrentWaypoint: React.Dispatch<React.SetStateAction<string | number | undefined>>

  // sidebar top offset caused by header etc.
  sidebarTopOffset?: number
  setSidebarTopOffset?: React.Dispatch<React.SetStateAction<number>>

  // sidebar menu height
  sidebarMenuHeight?: number
  setSidebarMenuHeight?: React.Dispatch<React.SetStateAction<number>>

  // sidebar wrapper margin top
  wrapperMarginTop?: number
  setWrapperMarginTop?: React.Dispatch<React.SetStateAction<number>>
}

const SidebarContext = createContext<SidebarContextType>({
  pageSections: [],
  setPageSections: () => { },
  // Curent waypoint
  currentWaypoint: undefined,
  setCurrentWaypoint: () => { },

  // sidebar top offset caused by header etc.
  sidebarTopOffset: 0,
  setSidebarTopOffset: () => { },

  // sidebar menu height
  sidebarMenuHeight: 0,
  setSidebarMenuHeight: () => { },

  // sidebar wrapper margin top
  wrapperMarginTop: 0,
  setWrapperMarginTop: () => { },
})

// SidebarProvider component
export interface SidebarProviderProps extends React.PropsWithChildren {
  pageSections?: PageSection[]
}

export const SidebarProvider = (props: SidebarProviderProps) => {
  const { children, pageSections: ps } = props
  const [pageSections, setPageSections] = useState<PageSection[] | undefined>(ps)
  const [sidebarTopOffset, setSidebarTopOffset] = React.useState<number>(0)
  const [sidebarMenuHeight, setSidebarMenuHeight] = React.useState<number>(0)
  const [currentWaypoint, setCurrentWaypoint] = React.useState<string | number>()
  const [wrapperMarginTop, setWrapperMarginTop] = React.useState<number>(0)

  useEffect(() => {
    if (ps)
      setPageSections(ps)      
  }, [ps])
  
  return (
    <SidebarContext.Provider
      value={{
        pageSections: pageSections || [],
        setPageSections: setPageSections,

        // Curent waypoint
        currentWaypoint,
        setCurrentWaypoint,

        // sidebar top offset caused by header etc.
        sidebarTopOffset,
        setSidebarTopOffset,

        // sidebar menu height
        sidebarMenuHeight,
        setSidebarMenuHeight,

        // sidebar wrapper margin top
        wrapperMarginTop,
        setWrapperMarginTop,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

// useSidebar hook
export interface useSidebarProps {
  pageSections?: PageSection[]
}

export const useSidebar = () => {
  const context = React.useContext(SidebarContext)

  if (!context || context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }

  return context
}
